// api
import type { EnergyPriceZone } from '@/api/generated/recalc/endpoints/v2/locations';

// shared components
import type { DanishAddressRegisterSelection } from '@/components/datafordeler/DanishAddressRegisterAutocomplete';

/**
 * The value shape stored in RHF for any field driven by `RHFAddressAutocomplete`.
 * Mirrors the backend's `BaseLocationDto`/`LocationDto` shape (using
 * `displayName` in place of a generic "label") so forms that need the full
 * Danish Address Register detail (e.g. creating a `Location` entity) can use
 * the value directly, while forms that only need a display label +
 * coordinates (e.g. a unit/gateway's lat/lng) can just read
 * `displayName`/`latitude`/`longitude` and ignore the rest.
 */
export interface DanishAddressFieldValue {
  displayName: string;
  street: string | null;
  houseNumber: string | null;
  floor: string | null;
  door: string | null;
  zipCode: string | null;
  city: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  coordinateAccuracyMeters: string | null;
  coordinateSource: string | null;
  timezone: string | null;
  energyPriceZone: EnergyPriceZone | null;
  municipalityCode: number | null;
  cadastreKey: string | null;
}

export const EMPTY_ADDRESS_VALUE: DanishAddressFieldValue = {
  displayName: '',
  street: null,
  houseNumber: null,
  floor: null,
  door: null,
  zipCode: null,
  city: null,
  country: null,
  latitude: null,
  longitude: null,
  coordinateAccuracyMeters: null,
  coordinateSource: null,
  timezone: null,
  energyPriceZone: null,
  municipalityCode: null,
  cadastreKey: null,
};

/**
 * Builds a `DanishAddressFieldValue` from a Danish Address Register
 * selection, matching the fields the backend `Location` entity expects.
 * Returns `EMPTY_ADDRESS_VALUE` if the selection is missing valid
 * coordinates (e.g. the user cleared the input).
 */
export function buildDanishAddressFieldValue(
  selection: DanishAddressRegisterSelection | null,
): DanishAddressFieldValue {
  const lat = Number(selection?.details?.coordinates?.lat);
  const lon = Number(selection?.details?.coordinates?.lon);

  if (!selection || Number.isNaN(lat) || Number.isNaN(lon)) {
    return EMPTY_ADDRESS_VALUE;
  }

  const cadastreKey =
    selection.details.ejerlav?.kode != null && selection.details.matrikelnummer
      ? `${selection.details.ejerlav.kode}_${selection.details.matrikelnummer}`
      : null;

  const parsedMunicipalityCode = parseInt(
    selection.details.municipalityCode,
    10,
  );
  const parsedZipCode = parseInt(selection.parsed.postnr, 10);

  return {
    displayName: selection.parsed.tekst || '',
    street: selection.parsed.vejnavn,
    houseNumber: selection.parsed.husnr || '',
    floor: selection.node.etagebetegnelse,
    door: selection.node.doerbetegnelse,
    zipCode: selection.parsed.postnr,
    city: selection.parsed.postnrnavn,
    country: 'DK',
    latitude: lat,
    longitude: lon,
    coordinateAccuracyMeters: null,
    coordinateSource: null,
    timezone: 'Europe/Copenhagen',
    energyPriceZone: Number.isFinite(parsedZipCode)
      ? parsedZipCode < 5000
        ? 'DK2'
        : 'DK1'
      : null,
    municipalityCode: Number.isFinite(parsedMunicipalityCode)
      ? parsedMunicipalityCode
      : null,
    cadastreKey,
  };
}
